import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, User, Clock, Eye, Download, Zap } from 'lucide-react';

const VideoCard = ({ 
  videoInfo, 
  selectedFormat, 
  onFormatChange, 
  formatOptions, 
  onDownload, 
  onStreamDownload, 
  isDownloading 
}) => {
  const { t, i18n } = useTranslation();
  const [mergeAudio, setMergeAudio] = useState(false);
  
  if (!videoInfo) return null;

  const {
    title,
    author,
    duration,
    thumbnail,
    thumbnailURL,
    viewCount,
    uploadDate,
    publishedAt,
    platform,
    channelTitle,
    authorName,
    authorUsername,
    likeCount,
    description,
    videoId,
    tweetId
  } = videoInfo;

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'youtube':
        return '🎬';
      case 'twitter':
        return '🐦';
      case 'bilibili':
        return '📺';
      case 'tiktok':
        return '🎵';
      case 'instagram':
        return '📷';
      default:
        return '🎥';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return t('info');
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const formatViewCount = (count) => {
    if (!count) return '';
    
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    } else {
      return count.toString();
    }
  };

  const formatUploadDate = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      // 处理不同的日期格式
      if (dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
        // 格式：20250527
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const date = new Date(`${year}-${month}-${day}`);
        return date.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US');
      } else {
        const date = new Date(dateStr);
        return date.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US');
      }
    } catch {
      return dateStr;
    }
  };

  // 获取显示的作者名称
  const getAuthorName = () => {
    if (platform === 'twitter') {
      return authorName || channelTitle || author;
    }
    return channelTitle || author || authorName;
  };

  // 获取显示的用户名
  const getUsername = () => {
    if (platform === 'twitter' && authorUsername) {
      return `@${authorUsername}`;
    }
    return '';
  };

  // 获取缩略图URL
  const getThumbnailUrl = () => {
    return thumbnailURL || thumbnail;
  };

  // 获取上传日期
  const getUploadDate = () => {
    return publishedAt || uploadDate;
  };

  // 获取本地化的观看次数文本
  const getViewCountText = (count) => {
    const formattedCount = formatViewCount(count);
    switch (i18n.language) {
      case 'zh':
        return `${formattedCount} 次观看`;
      case 'en':
        return `${formattedCount} views`;
      case 'ja':
        return `${formattedCount} 回視聴`;
      case 'ko':
        return `${formattedCount} 조회수`;
      case 'es':
        return `${formattedCount} visualizaciones`;
      case 'fr':
        return `${formattedCount} vues`;
      case 'de':
        return `${formattedCount} Aufrufe`;
      case 'ru':
        return `${formattedCount} просмотров`;
      default:
        return `${formattedCount} views`;
    }
  };

  // 获取本地化的点赞数文本
  const getLikeCountText = (count) => {
    const formattedCount = formatViewCount(count);
    switch (i18n.language) {
      case 'zh':
        return `${formattedCount} 点赞`;
      case 'en':
        return `${formattedCount} likes`;
      case 'ja':
        return `${formattedCount} いいね`;
      case 'ko':
        return `${formattedCount} 좋아요`;
      case 'es':
        return `${formattedCount} me gusta`;
      case 'fr':
        return `${formattedCount} j'aime`;
      case 'de':
        return `${formattedCount} Gefällt mir`;
      case 'ru':
        return `${formattedCount} лайков`;
      default:
        return `${formattedCount} likes`;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden animate-slide-up">
      <div className="relative aspect-video bg-gray-100">
        {getThumbnailUrl() ? (
          <img
            src={getThumbnailUrl()}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        
        <div 
          className="absolute inset-0 flex items-center justify-center bg-gray-200"
          style={{ display: getThumbnailUrl() ? 'none' : 'flex' }}
        >
          <Play className="w-16 h-16 text-gray-400" />
        </div>

        <div className="absolute top-3 left-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-md text-sm flex items-center gap-1">
          <span>{getPlatformIcon(platform)}</span>
          <span className="capitalize">{platform}</span>
        </div>

        {duration > 0 && (
          <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-md text-sm">
            {formatDuration(duration)}
          </div>
        )}
      </div>

      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
          {title}
        </h3>

        <div className="space-y-3 mb-4">
          {/* 作者信息 */}
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-500" />
            <div className="flex flex-col">
              <span className="font-medium text-gray-900">{getAuthorName()}</span>
              {getUsername() && (
                <span className="text-sm text-gray-500">{getUsername()}</span>
              )}
            </div>
          </div>

          {/* 统计信息 */}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            {viewCount !== null && viewCount > 0 && (
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{getViewCountText(viewCount)}</span>
              </div>
            )}
            
            {likeCount !== null && likeCount > 0 && (
              <div className="flex items-center gap-1">
                <span>❤️</span>
                <span>{getLikeCountText(likeCount)}</span>
              </div>
            )}
            
            {getUploadDate() && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{formatUploadDate(getUploadDate())}</span>
              </div>
            )}
          </div>

          {/* 视频ID信息 */}
          {(videoId || tweetId) && (
            <div className="text-xs text-gray-500">
              ID: {videoId || tweetId}
            </div>
          )}

          {/* 描述信息 */}
          {description && (
            <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
              <p className="line-clamp-3">{description}</p>
            </div>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {i18n.language === 'zh' ? '选择视频质量' : 
             i18n.language === 'en' ? 'Select Video Quality' :
             i18n.language === 'ja' ? '動画品質を選択' :
             i18n.language === 'ko' ? '비디오 품질 선택' :
             i18n.language === 'es' ? 'Seleccionar Calidad de Video' :
             i18n.language === 'fr' ? 'Sélectionner la Qualité Vidéo' :
             i18n.language === 'de' ? 'Videoqualität Auswählen' :
             i18n.language === 'ru' ? 'Выбрать Качество Видео' :
             'Select Video Quality'}
          </label>
          <select
            value={selectedFormat}
            onChange={(e) => onFormatChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          >
            {formatOptions.map((option) => (
              option.isGroupLabel ? (
                <option key={option.value} value={option.value} disabled className="font-bold bg-gray-100">
                  --- {option.label} ---
                </option>
              ) : (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
              )
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {i18n.language === 'zh' ? `已选择格式ID: ${selectedFormat}` : 
             i18n.language === 'en' ? `Selected format ID: ${selectedFormat}` :
             i18n.language === 'ja' ? `選択したフォーマットID: ${selectedFormat}` :
             i18n.language === 'ko' ? `선택한 형식 ID: ${selectedFormat}` :
             i18n.language === 'es' ? `ID de formato seleccionado: ${selectedFormat}` :
             i18n.language === 'fr' ? `ID de format sélectionné: ${selectedFormat}` :
             i18n.language === 'de' ? `Ausgewählte Format-ID: ${selectedFormat}` :
             i18n.language === 'ru' ? `Выбранный ID формата: ${selectedFormat}` :
             `Selected format ID: ${selectedFormat}`}
          </p>
        </div>

        <div className="mb-4 flex items-center">
          <input
            id="merge-audio"
            type="checkbox"
            checked={mergeAudio}
            onChange={(e) => setMergeAudio(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label htmlFor="merge-audio" className="ml-2 text-sm text-gray-700">
            {i18n.language === 'zh' ? '自动合并音频（如果视频不包含音轨）' : 
             i18n.language === 'en' ? 'Auto merge audio (if video has no audio track)' :
             i18n.language === 'ja' ? '自動的に音声をマージする（動画に音声トラックがない場合）' :
             i18n.language === 'ko' ? '자동 오디오 병합 (비디오에 오디오 트랙이 없는 경우)' :
             i18n.language === 'es' ? 'Fusionar audio automáticamente (si el video no tiene pista de audio)' :
             i18n.language === 'fr' ? 'Fusion audio automatique (si la vidéo n\'a pas de piste audio)' :
             i18n.language === 'de' ? 'Audio automatisch zusammenführen (wenn Video keine Audiospur hat)' :
             i18n.language === 'ru' ? 'Автоматически объединять аудио (если в видео нет аудиодорожки)' :
             'Auto merge audio (if video has no audio track)'}
          </label>
        </div>

        <div className="flex gap-3">
        <button
          onClick={() => onDownload(mergeAudio)}
          disabled={isDownloading}
          className={`
              flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all
            ${isDownloading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800'
            }
            text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5
          `}
        >
          <Download className={`w-5 h-5 ${isDownloading ? 'animate-pulse' : ''}`} />
          <span>{isDownloading ? t('downloading') : t('download')}</span>
        </button>
          
          <button
            onClick={() => onStreamDownload(mergeAudio)}
            disabled={isDownloading}
            title={t('streamDownload')}
            className={`
              flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all
              ${isDownloading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
              }
              text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5
            `}
          >
            <Zap className={`w-5 h-5 ${isDownloading ? 'animate-pulse' : ''}`} />
            <span>{t('stream')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoCard; 